import { Close } from '@mui/icons-material';
import { Avatar, Button, SearchBar, Tag } from 'finara-components';
import { useState } from 'react';

import { ErrorState, LoadingState } from '@/components/common';
import { useAdminPendingActions } from '@/hooks/queries/useAnalytics';

interface AllPendingActionsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const getActionColor = (actionType: string) => {
  switch (actionType.toLowerCase()) {
    case 'kyc_pending':
    case 'kyc':
      return 'text-yellow-600';
    case 'advance_pending':
    case 'adelanto':
      return 'text-red-600';
    case 'data_change_pending':
    case 'bancario':
      return 'text-blue-600';
    default:
      return 'text-muted';
  }
};

const getPriorityVariant = (priority: string) => {
  switch (priority.toLowerCase()) {
    case 'high':
      return 'red';
    case 'medium':
      return 'yellow';
    case 'low':
      return 'gray';
    default:
      return 'gray';
  }
};

function getInitials(name: string): string {
  const names = name.split(' ');
  if (names.length >= 2) {
    return `${names[0]?.[0] || ''}${names[1]?.[0] || ''}`.toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
}

export const AllPendingActionsModal: React.FC<AllPendingActionsModalProps> = ({
  isOpen,
  onClose,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const limit = 20;

  const { data, isLoading, error, refetch } = useAdminPendingActions({
    page: currentPage,
    limit,
    orderDirection: 'DESC',
  });

  if (!isOpen) return null;

  const actions = data?.data || [];
  const filteredActions = actions.filter(action => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      action.employeeName.toLowerCase().includes(searchLower) ||
      action.department.toLowerCase().includes(searchLower) ||
      action.message.toLowerCase().includes(searchLower)
    );
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <button
        className="absolute inset-0 bg-black opacity-60 cursor-default"
        onClick={onClose}
        onKeyDown={e => {
          if (e.key === 'Escape') onClose();
        }}
        aria-label="Close modal"
        type="button"
      />
      {/* Modal Content */}
      <div className="relative bg-surface border border-black/10 dark:border-white/15 rounded-lg shadow-2xl shadow-black/60 w-full max-w-5xl max-h-[90vh] overflow-hidden z-10">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-xl font-bold text-brand-white">Todas las Alertas Pendientes</h2>
            <p className="text-sm text-muted mt-1">Total de {data?.count} acciones pendientes</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-muted hover:text-brand-white rounded-lg hover:bg-black/5 dark:hover:bg-white/5"
          >
            <Close />
          </button>
        </div>

        {/* Search Bar */}
        <div className="p-6 border-b border-border">
          <SearchBar
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder="Buscar por empleado, departamento o acción..."
            className="w-full"
          />
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 280px)' }}>
          {isLoading ? (
            <LoadingState message="Cargando acciones pendientes..." />
          ) : error ? (
            <ErrorState
              title="Error al cargar acciones pendientes"
              message="No se pudieron cargar las acciones pendientes. Por favor, intenta nuevamente."
              error={error}
              showRetry
              onRetry={() => void refetch()}
            />
          ) : filteredActions.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-muted">No se encontraron acciones pendientes</p>
            </div>
          ) : (
            <div className="border border-border rounded-lg overflow-hidden">
              {filteredActions.map((action, index) => (
                <div
                  key={action.employeeId}
                  className={`flex items-center justify-between px-4 py-3 hover:bg-black/5 dark:hover:bg-white/5 transition-colors ${index < filteredActions.length - 1 ? 'border-b border-border' : ''}`}
                >
                  <div className="md:flex items-center justify-between md:w-8/12">
                    <div className="flex items-center gap-3">
                      <Avatar
                        initials={getInitials(action.employeeName)}
                        title={action.employeeName}
                        subtitle={action.department}
                        size="large"
                        avatarBgColor="bg-brand-blue/30"
                        avatarTextColor="text-brand-white"
                      />
                    </div>
                    <div className="md:mx-4 mt-2 md:!mt-0">
                      <div className={`text-[12.25px] font-medium ${getActionColor(action.actionType)}`}>
                        {action.message}
                      </div>
                      <div className="text-[10.5px] text-muted">
                        {action.daysPending} días pendiente
                      </div>
                    </div>
                  </div>
                  <Tag variant={getPriorityVariant(action.priority)}>{action.priority}</Tag>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {data && data.count > limit && (
          <div className="flex items-center justify-between p-6 border-t border-border">
            <div className="text-sm text-muted">
              Página {currentPage} • Mostrando {filteredActions.length} de {data.count}
            </div>
            <div className="flex gap-2">
              <Button
                variant="white"
                size="small"
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
              >
                Anterior
              </Button>
              <Button
                variant="white"
                size="small"
                onClick={() => setCurrentPage(prev => prev + 1)}
                disabled={currentPage * limit >= data.count}
              >
                Siguiente
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
